import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfile } from '@/hooks/useUserProfile';

export interface ExistingGuideMapping {
  [sopId: string]: string; // sopId -> guideId
}

async function fetchExistingGuides(organizationId: string): Promise<ExistingGuideMapping> {
  const { data, error } = await supabase
    .from('automation_guides')
    .select('id, sop_id')
    .eq('organization_id', organizationId);

  if (error) {
    throw new Error(`Error fetching existing guides: ${error.message}`);
  }

  const mapping: ExistingGuideMapping = {};
  (data || []).forEach(g => {
    mapping[g.sop_id] = g.id;
  });

  return mapping;
}

interface UseExistingAutomationGuidesOptions {
  enabled?: boolean;
}

/**
 * Shared hook for fetching existing automation guides mapping (SOP ID -> Guide ID).
 * Used to determine which SOPs have guides and show "Open Guide" vs "Generate Guide" buttons.
 * Data is cached for 5 minutes.
 *
 * Usage:
 * - In components: const { data: existingGuides } = useExistingAutomationGuides();
 * - Check if SOP has guide: existingGuides?.[sopId]
 * - When guides change: call invalidateExistingGuides()
 */
export function useExistingAutomationGuides(options: UseExistingAutomationGuidesOptions = {}) {
  const { organization } = useUserProfile();
  const orgId = organization?.id;

  return useQuery({
    queryKey: ['existing-automation-guides', orgId],
    queryFn: () => fetchExistingGuides(orgId!),
    enabled: (options.enabled ?? true) && !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get the invalidation function for existing automation guides cache.
 * Call this after generating or deleting automation guides.
 */
export function useInvalidateExistingAutomationGuides() {
  const queryClient = useQueryClient();
  const { organization } = useUserProfile();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['existing-automation-guides', organization?.id] });
  };
}

/**
 * Utility to invalidate existing automation guides cache from outside React components.
 */
export function invalidateExistingAutomationGuidesCache(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId?: string
) {
  if (organizationId) {
    queryClient.invalidateQueries({ queryKey: ['existing-automation-guides', organizationId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ['existing-automation-guides'] });
  }
}
